import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import {
  Sun, CloudRain, Cloud, Snowflake, ArrowLeft, Share2, Download,
  Music, Clapperboard, Tv, BookOpen, Newspaper, Fuel, TrendingUp, MessageCircle,
  Film, Bookmark, BookmarkCheck, Tv2,
} from "lucide-react";
import { fetchSnapshot, postTimeJump, postNarrate, fetchConfig, addFavourite, removeFavourite, listFavourites } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { getStrings, isRTL, detectLanguageFromSnapshot } from "@/lib/i18n";
import { displayTemp } from "@/lib/weather";
import { useAuth } from "@/context/AuthContext";
import ShareCard from "@/components/ShareCard";
import ShareButtons from "@/components/ShareButtons";
import ShareCardButton from "@/components/ShareCardButton";
import ReferralCard from "@/components/ReferralCard";
import RadioBulletin from "@/components/RadioBulletin";
import HistoricalMedia from "@/components/HistoricalMedia";
import AuthMenu from "@/components/AuthMenu";
import TVCastOverlay from "@/components/TVCastOverlay";
import PhoneQrPopover from "@/components/PhoneQrPopover";
import QrModal from "@/components/QrModal";
import { isTvBrowser } from "@/lib/device";

/**
 * Top-3 "Soundtrack of the Day" card — the whole tile is a Spotify link.
 * On Samsung Tizen / LG webOS (no Widevine DRM), the direct Spotify click
 * fails, so the card opens a QR modal instead so the user can beam it to
 * their phone. Desktop keeps the direct click behaviour.
 */
function SoundtrackCard({ song, rank, spotifyUrl, openLabel }) {
  const [open, setOpen] = useState(false);
  const isTv = isTvBrowser();
  const modalLabel = `${song.title} — ${song.artist}`;
  const commonClass =
    "border-r-2 border-b-2 border-ink bg-paper-alt hover:bg-ink hover:text-paper p-3 transition-colors group flex flex-col text-left";

  const inner = (
    <>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted group-hover:text-paper/70 mb-2">
        <span className="w-6 h-6 border-2 border-current inline-flex items-center justify-center">
          ▶
        </span>
        #{rank}
      </div>
      <div className="font-headline text-base font-bold leading-tight">{song.title}</div>
      {song.title_local && song.title_local !== song.title && (
        <div className="font-headline text-sm leading-tight mt-0.5" dir="auto">
          {song.title_local}
        </div>
      )}
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted group-hover:text-paper/70 mt-1">
        {song.artist}
      </div>
      <div className="mt-auto pt-2 font-mono text-[10px] uppercase tracking-widest text-press-red group-hover:text-press-red">
        {isTv ? "Scan → phone" : openLabel}
      </div>
    </>
  );

  if (isTv) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={commonClass}
          data-testid={`soundtrack-card-${rank}`}
        >
          {inner}
        </button>
        <QrModal
          url={spotifyUrl}
          label={modalLabel}
          open={open}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  return (
    <a
      href={spotifyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={commonClass}
      data-testid={`soundtrack-card-${rank}`}
    >
      {inner}
    </a>
  );
}

function weatherIcon(cond = "") {
  const c = cond.toLowerCase();
  if (c.includes("snow")) return <Snowflake size={40} />;
  if (c.includes("rain") || c.includes("storm") || c.includes("shower")) return <CloudRain size={40} />;
  if (c.includes("cloud") || c.includes("overcast") || c.includes("fog")) return <Cloud size={40} />;
  return <Sun size={40} />;
}

export default function Dashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaSeen, setMediaSeen] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [config, setConfig] = useState({ amazon_tag: "" });
  const [isFavourite, setIsFavourite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [castOpen, setCastOpen] = useState(false);
  const { user, showSignIn } = useAuth();
  const shareRef = useRef(null);

  useEffect(() => {
    fetchConfig().then(setConfig).catch(() => {});
    try { setMediaSeen(!!localStorage.getItem("tc:media-seen")); } catch { /* ignore */ }
  }, []);

  // Refresh favourite state whenever snapshot or user changes
  useEffect(() => {
    if (!user || !doc?.id) { setIsFavourite(false); return; }
    listFavourites()
      .then((list) => setIsFavourite(list.some((f) => f.snapshot_id === doc.id)))
      .catch(() => {});
  }, [user, doc?.id]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const cached = id ? sessionStorage.getItem(`tc:${id}`) : null;
        if (cached) {
          const parsed = JSON.parse(cached);
          if (!cancelled) setDoc(parsed);
        } else if (id) {
          const d = await fetchSnapshot(id);
          if (!cancelled) setDoc(d);
        } else {
          navigate("/");
        }
        if (!cancelled && id) trackEvent("page_view", { page: "dashboard", snapshot_id: id });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load snapshot");
        navigate("/");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const regen = async () => {
    if (!doc) return;
    setLoading(true);
    try {
      const data = await postTimeJump({ date: doc.date, region: doc.region });
      sessionStorage.setItem(`tc:${data.id}`, JSON.stringify(data));
      navigate(`/snapshot/${data.id}`);
    } catch (e) {
      toast.error("Failed to reload");
    } finally {
      setLoading(false);
    }
  };

  const toggleFavourite = async () => {
    if (!doc) return;
    if (!user) {
      trackEvent("favourite_signin_prompt", { snapshot_id: doc.id });
      toast.info("Sign in to save this date");
      showSignIn(`/snapshot/${doc.id}`);
      return;
    }
    setFavLoading(true);
    try {
      if (isFavourite) {
        await removeFavourite(doc.id);
        setIsFavourite(false);
        toast.success("Removed from favourites");
        trackEvent("favourite_remove", { snapshot_id: doc.id });
      } else {
        await addFavourite(doc.id);
        setIsFavourite(true);
        toast.success("Saved to favourites");
        trackEvent("favourite_add", { snapshot_id: doc.id });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update favourite");
    } finally {
      setFavLoading(false);
    }
  };

  const download = async () => {
    if (!shareRef.current) return;
    setDownloading(true);
    trackEvent("share_download", { snapshot_id: doc.id });
    try {
      const canvas = await html2canvas(shareRef.current, {
        backgroundColor: "#F2ECE4",
        scale: 2,
        useCORS: true,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `timecapsule-${doc.date}-${doc.region}.png`;
      a.click();
      toast.success("Snapshot saved to your device");
    } catch (e) {
      toast.error("Could not export image");
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !doc) {
    return <LoadingView />;
  }

  const s = doc.snapshot;
  const meta = s._meta || {};
  const language = detectLanguageFromSnapshot(s, meta.region);
  const rtl = isRTL(language);
  const t = getStrings(language);

  return (
    <div
      className={`min-h-screen w-full ${rtl ? "tc-arabic" : ""}`}
      dir={rtl ? "rtl" : "ltr"}
      data-testid="dashboard-page"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <button
              data-testid="back-btn"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              <ArrowLeft size={14} /> {t.backToDesk}
            </button>
            <AuthMenu returnPath={`/snapshot/${doc.id}`} />
          </div>
          <div className="flex gap-2">
            <button
              data-testid="favourite-btn"
              onClick={toggleFavourite}
              disabled={favLoading}
              aria-pressed={isFavourite}
              title={isFavourite ? "Remove from favourites" : "Save to favourites"}
              className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 shadow-hard-sm hover:shadow-hard-hover disabled:opacity-60 ${isFavourite ? "bg-press-red text-paper" : "bg-paper-surface"}`}
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              {isFavourite ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
              <span className="hidden sm:inline">{isFavourite ? "Saved" : "Save"}</span>
            </button>
            <button
              data-testid="regen-btn"
              onClick={regen}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              <Newspaper size={14} /> {t.reprint}
            </button>
            <button
              data-testid="watch-cinema-btn"
              onClick={() => { trackEvent("cinema_open", { snapshot_id: doc.id }); navigate(`/watch/${doc.id}`); }}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-ink text-paper shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              title="Watch this day as a cinematic episode"
            >
              <Tv2 size={14} /> Watch
              <span className="hidden md:inline text-[9px] uppercase tracking-widest border-l border-paper/40 pl-2 ml-1 opacity-70">Preview</span>
            </button>
            <button
              data-testid="media-btn"
              onClick={() => {
                trackEvent("media_open", { snapshot_id: doc.id, date: doc.date, region: doc.region });
                try { localStorage.setItem("tc:media-seen", "1"); } catch { /* ignore */ }
                setMediaSeen(true);
                setMediaOpen(true);
              }}
              className={`relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-alt shadow-hard-sm hover:shadow-hard-hover ${!mediaSeen ? "tc-media-pulse" : ""}`}
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              <Film size={14} /> {t.media}
              {!mediaSeen && (
                <span
                  aria-hidden
                  data-testid="media-new-badge"
                  className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center justify-center bg-press-red text-paper border-2 border-ink font-mono text-[9px] uppercase tracking-widest px-1.5 leading-tight shadow-hard-sm"
                >
                  {rtl ? "جديد" : "New"}
                </span>
              )}
            </button>
            <button
              data-testid="cast-btn"
              onClick={() => { trackEvent("dashboard_cast_click", { snapshot_id: doc.id }); setCastOpen(true); }}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-ink text-paper shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              title="Watch this snapshot on your TV or another device via QR handoff"
            >
              <Tv size={14} /> Watch on TV
            </button>
            <button
              data-testid="share-btn"
              onClick={() => { trackEvent("share_open", { snapshot_id: doc.id, date: doc.date, region: doc.region }); setShareOpen(true); }}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-press-red text-paper shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              <Share2 size={14} /> {t.share}
            </button>
          </div>
        </div>

        {/* Masthead */}
        <header className="border-b-4 border-double border-ink pb-4 mb-6 text-center tc-fade-up" data-testid="dashboard-masthead">
          <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.25em] text-ink-muted mb-2">
            <span>{meta.region?.flag} {meta.region?.name} · {t.edition}</span>
            <span className="hidden sm:inline">{t.price}</span>
            <span>{t.volume} 24</span>
          </div>
          <h1 className="font-headline font-black uppercase leading-none tracking-tighter text-4xl sm:text-6xl md:text-7xl">
            The Time Capsule
          </h1>
          <div className="mt-2 font-mono uppercase tracking-[0.3em] text-xs sm:text-sm text-ink-muted">
            {s.headline_dateline || meta.long_date}
          </div>
          {s.front_page_quote && (
            <p className="mt-4 font-body italic text-lg sm:text-xl text-ink-secondary max-w-3xl mx-auto">
              “{s.front_page_quote}”
            </p>
          )}
        </header>

        {/* Circulation ticker */}
        <div className="mb-4 border-2 border-ink bg-ink text-paper overflow-hidden" data-testid="circulation-ticker">
          <div className="tc-marquee font-mono text-[11px] uppercase tracking-widest py-2">
            {[
              `Paper Route · ${meta.region?.name || "Global"} Edition`,
              `Transmitted via wire · ${meta.weekday || ""} ${meta.long_date || ""}`,
              `Circulation: 1 reader · You`,
              `Vol. 24 · No. ${(doc.id || "").slice(0, 4)}`,
              `Late edition · Composed in ${Math.max(3, Math.round((JSON.stringify(s).length / 1000)))} seconds`,
            ].concat([
              `Paper Route · ${meta.region?.name || "Global"} Edition`,
              `Transmitted via wire · ${meta.weekday || ""} ${meta.long_date || ""}`,
              `Circulation: 1 reader · You`,
            ]).map((tag, i) => (<span key={i} className="px-6">◆ {tag}</span>))}
          </div>
        </div>

        {/* Radio bulletin */}
        <RadioBulletin snapshotId={doc.id} meta={meta} t={t} />

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border-t-2 border-l-2 border-ink">
          {/* News (left, wide) */}
          <section className="md:col-span-8 border-r-2 border-b-2 border-ink p-6 sm:p-8 bg-paper-surface tc-fade-up tc-delay-1" data-testid="section-news">
            <SectionTitle icon={<Newspaper size={16} />} label={t.frontPage} />
            <div className="space-y-6">
              {(s.news || []).slice(0, 3).map((n, i) => (
                <article key={i} className="pb-4 border-b border-dashed border-ink last:border-b-0" data-testid={`news-${i}`}>
                  <h2 className={`font-headline font-bold uppercase leading-tight ${i === 0 ? "text-3xl sm:text-4xl" : "text-2xl"}`}>
                    {n.headline}
                  </h2>
                  <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-ink-muted">
                    {n.byline || t.storyByline} · {meta.long_date}
                  </div>
                  <p className={`mt-3 font-body text-base sm:text-lg leading-relaxed text-ink-secondary ${i === 0 ? "drop-cap" : ""}`}>
                    {n.summary}
                  </p>
                  {n.wiki_slug && (
                    <a
                      href={`https://${rtl ? "ar" : "en"}.wikipedia.org/wiki/${encodeURIComponent(n.wiki_slug)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("news_wiki_click", { slug: n.wiki_slug })}
                      className="inline-block mt-2 font-mono text-[10px] uppercase tracking-widest text-press-red hover:text-press-redHover"
                      data-testid={`news-wiki-${i}`}
                    >
                      {rtl ? "اقرأ المزيد على ويكيبيديا ↗" : "Read more on Wikipedia ↗"}
                    </a>
                  )}
                </article>
              ))}
            </div>
          </section>

          {/* Weather sidebar */}
          <aside className="md:col-span-4 border-r-2 border-b-2 border-ink p-6 bg-paper-alt tc-fade-up tc-delay-2" data-testid="section-weather">
            <SectionTitle label={t.weather} />
            {(() => { const dt = displayTemp(s.weather, meta.region); return (
            <div className="flex items-start gap-4">
              <div className="mt-1 text-ink">{weatherIcon(s.weather?.condition)}</div>
              <div>
                <div className="font-headline text-4xl font-bold" data-testid="weather-high">{dt.high}°<span className="text-ink-muted text-xl">/{dt.low}°{dt.unit}</span></div>
                <div className="font-mono uppercase tracking-widest text-xs text-ink-muted mt-1">{s.weather?.condition}</div>
                <div className="font-body italic mt-1">{s.weather?.city}</div>
              </div>
            </div>
            ); })()}
            <p className="mt-4 font-body text-sm text-ink-secondary leading-relaxed">{s.weather?.description}</p>

            <div className="mt-6 border-t border-dashed border-ink pt-4">
              <SectionTitle label={t.onTheWire} />
              <div className="font-mono text-xs uppercase tracking-widest text-ink-muted">
                {meta.region?.name} · {meta.weekday}
              </div>
            </div>
          </aside>

          {/* Music */}
          <section className="md:col-span-6 border-r-2 border-b-2 border-ink p-6 sm:p-8 bg-paper-surface tc-fade-up tc-delay-3" data-testid="section-music">
            <SectionTitle icon={<Music size={16} />} label={s.music?.chart_name || t.chartFallback} />
            <ol className="space-y-2">
              {(s.music?.top_songs || []).map((song, i) => (
                <li key={i} className="flex items-baseline gap-3 border-b border-dotted border-ink pb-2 last:border-b-0" data-testid={`song-${song.rank || i + 1}`}>
                  <span className="font-headline text-3xl font-black w-10 text-press-red">{song.rank || i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-headline text-lg font-bold leading-tight">{song.title}</div>
                    {song.title_local && song.title_local !== song.title && (
                      <div className="font-headline text-base leading-tight" dir="auto" data-testid={`song-title-local-${song.rank || i + 1}`}>{song.title_local}</div>
                    )}
                    <div className="font-mono text-[11px] uppercase tracking-widest text-ink-muted">
                      {song.artist}
                      {song.artist_local && song.artist_local !== song.artist && (
                        <span dir="auto"> · <span className="normal-case tracking-normal" data-testid={`song-artist-local-${song.rank || i + 1}`}>{song.artist_local}</span></span>
                      )}
                    </div>
                  </div>
                  <PhoneQrPopover
                    url={`https://open.spotify.com/search/${encodeURIComponent(song.spotify_query || `${song.title} ${song.artist}`)}`}
                    label={`${song.title} — ${song.artist}`}
                    tvLabel={t.listen}
                    testId={`song-listen-qr-${song.rank || i + 1}`}
                  >
                    <a
                      href={`https://open.spotify.com/search/${encodeURIComponent(song.spotify_query || `${song.title} ${song.artist}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red shrink-0"
                      data-testid={`song-listen-${song.rank || i + 1}`}
                    >
                      {t.listen}
                    </a>
                  </PhoneQrPopover>
                  <a
                    href={`https://music.apple.com/search?term=${encodeURIComponent(song.spotify_query || `${song.title} ${song.artist}`)}${config.apple_music_token ? `&at=${encodeURIComponent(config.apple_music_token)}` : ''}`}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={() => trackEvent("song_apple_click", { rank: song.rank || i + 1 })}
                    className="font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red shrink-0"
                    data-testid={`song-apple-${song.rank || i + 1}`}
                  >
                    Apple ↗
                  </a>
                </li>
              ))}
            </ol>

            {/* Soundtrack of the Day — Top 3 Play-on-Spotify cards */}
            {(s.music?.top_songs || []).slice(0, 3).length > 0 && (
              <div className="mt-5 pt-4 border-t-2 border-dashed border-ink" data-testid="soundtrack-of-the-day">
                <div className="flex items-center gap-2 mb-3">
                  <span className="ink-stamp red">{t.soundtrack}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted hidden sm:inline">{t.soundtrackHint}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border-t-2 border-l-2 border-ink">
                  {(s.music?.top_songs || []).slice(0, 3).map((song, i) => {
                    const query = song.spotify_query || `${song.title} ${song.artist}`;
                    const spotifyUrl = `https://open.spotify.com/search/${encodeURIComponent(query)}`;
                    return (
                      <SoundtrackCard
                        key={i}
                        song={song}
                        rank={song.rank || i + 1}
                        spotifyUrl={spotifyUrl}
                        openLabel={t.openSpotify}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Cinema */}
          <section className="md:col-span-3 border-r-2 border-b-2 border-ink p-6 bg-paper-surface tc-fade-up tc-delay-4" data-testid="section-cinema">
            <SectionTitle icon={<Clapperboard size={16} />} label={t.atCinema} />
            <ul className="space-y-3">
              {(s.cinema?.top_movies || []).map((m, i) => {
                const year = meta?.long_date ? meta.long_date.slice(-4) : "";
                const latinTitle = (m.title || m.search_query || "").trim();
                const localTitle = (m.title_local || m.title || "").trim();
                const isAr = language === "Arabic";
                // Detect the film's own language, not just the region — a UAE
                // audience mostly watches Hollywood + Bollywood, so `Avatar`
                // and `3 Idiots` must route to JustWatch (which supports AE),
                // not Shahid. Only films with real Arabic titles get Shahid.
                const hasArabicTitle = /[\u0600-\u06FF]/.test(m.title_local || "");
                const jwCountry = isAr ? "ae" : (config.justwatch_country || "us");
                // For Arabic-origin films, YouTube full-movie search is best;
                // for Hollywood/Bollywood, use the standard trailer search.
                const ytPrimaryQuery = hasArabicTitle
                  ? `${localTitle} فيلم كامل`.trim()
                  : `${latinTitle} ${year} trailer`.trim();
                const jwQuery = `${latinTitle} ${year}`.trim();
                const wikiLang = hasArabicTitle ? "ar" : "en";
                const wikiQuery = hasArabicTitle ? localTitle : `${latinTitle} ${year} film`;
                const trailerUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(ytPrimaryQuery)}`;
                const streamUrl = hasArabicTitle
                  ? `https://shahid.mbc.net/en/search-page?query=${encodeURIComponent(localTitle || latinTitle)}`
                  : `https://www.justwatch.com/${jwCountry}/search?q=${encodeURIComponent(jwQuery)}`;
                const streamHost = hasArabicTitle ? "shahid" : "justwatch";
                // Affiliate revenue links: Apple TV (PHG) + Amazon Prime Video.
                // Only surfaced when the app has the tag/token configured.
                const appleQuery = encodeURIComponent(latinTitle || localTitle);
                const appleTvUrl = `https://tv.apple.com/${jwCountry}/search?term=${appleQuery}${config.apple_music_token ? `&at=${encodeURIComponent(config.apple_music_token)}` : ""}`;
                const amazonDomain = config.amazon_domain || "www.amazon.com";
                const amazonPrimeUrl = config.amazon_tag
                  ? `https://${amazonDomain}/s?k=${encodeURIComponent(`${latinTitle} ${year}`)}&tag=${encodeURIComponent(config.amazon_tag)}`
                  : null;
                const wikiUrl = `https://${wikiLang}.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(wikiQuery.trim())}&go=Go`;
                return (
                <li key={i} className="border-b border-dotted border-ink pb-2 last:border-b-0" data-testid={`movie-${i}`}>
                  <div className="font-headline text-lg font-bold leading-tight">{m.title}</div>
                  {m.title_local && m.title_local !== m.title && (
                    <div className="font-headline text-base leading-tight" dir="auto" data-testid={`movie-title-local-${i}`}>{m.title_local}</div>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{m.genre}</div>
                  <div className="font-body italic text-sm text-ink-secondary mt-1">{m.note}</div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    <a
                      href={trailerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("movie_trailer_click", { title: m.title })}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-press-red hover:underline"
                      data-testid={`movie-trailer-${i}`}
                    >
                      ▶ {t.watchTrailer}
                    </a>
                    <PhoneQrPopover
                      url={streamUrl}
                      label={`${t.whereToStream} — ${latinTitle}`}
                      tvLabel={t.whereToStream}
                      testId={`movie-justwatch-qr-${i}`}
                    >
                      <a
                        href={streamUrl}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => trackEvent(`movie_${streamHost}_click`, { title: m.title })}
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red"
                        data-testid={`movie-justwatch-${i}`}
                      >
                        {t.whereToStream} ↗
                      </a>
                    </PhoneQrPopover>
                    <PhoneQrPopover
                      url={appleTvUrl}
                      label={`Apple TV — ${latinTitle}`}
                      tvLabel="Apple TV"
                      testId={`movie-appletv-qr-${i}`}
                    >
                      <a
                        href={appleTvUrl}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => trackEvent("movie_appletv_click", { title: m.title, has_token: !!config.apple_music_token })}
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red"
                        data-testid={`movie-appletv-${i}`}
                      >
                        Apple TV ↗
                      </a>
                    </PhoneQrPopover>
                    {amazonPrimeUrl && (
                      <PhoneQrPopover
                        url={amazonPrimeUrl}
                        label={`Amazon — ${latinTitle}`}
                        tvLabel="Amazon"
                        testId={`movie-prime-qr-${i}`}
                      >
                        <a
                          href={amazonPrimeUrl}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                          onClick={() => trackEvent("movie_prime_click", { title: m.title, has_tag: !!config.amazon_tag })}
                          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red"
                          data-testid={`movie-prime-${i}`}
                        >
                          Amazon ↗
                        </a>
                      </PhoneQrPopover>
                    )}
                    <a
                      href={wikiUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("movie_wiki_click", { title: m.title })}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red"
                      data-testid={`movie-wiki-${i}`}
                    >
                      {t.aboutFilm} ↗
                    </a>
                  </div>
                </li>
              );})}
            </ul>
          </section>

          {/* TV */}
          <section className="md:col-span-3 border-r-2 border-b-2 border-ink p-6 bg-paper-surface tc-fade-up tc-delay-5" data-testid="section-tv">
            <SectionTitle icon={<Tv size={16} />} label={t.onTheTube} />
            <ul className="space-y-3">
              {(s.tv?.popular_shows || []).map((tv, i) => {
                const year = meta?.long_date ? meta.long_date.slice(-4) : "";
                const isAr = language === "Arabic";
                const rawTitle = (tv.title || "").trim();
                const network = (tv.network || "").trim();
                const isNonLatin = (str) => /[^\u0000-\u024F]/.test(str);
                const titleIsNonLatin = isNonLatin(rawTitle);
                const networkIsNonLatin = isNonLatin(network);
                const latinTitle = !titleIsNonLatin ? rawTitle : "";
                const latinNetwork = !networkIsNonLatin ? network : "";
                const jwFallback = (latinTitle || latinNetwork).trim();
                // Route Arabic-script titles to Shahid; Latin titles (imported
                // Hollywood/Bollywood shows on UAE TV) go to region-appropriate
                // JustWatch — AE for Arabic regions, else the configured default.
                const hasArabicTitle = /[\u0600-\u06FF]/.test(rawTitle);
                const jwCountry = isAr ? "ae" : (config.justwatch_country || "us");
                // YouTube: for Arabic-script titles, add "حلقات" (episodes);
                // for Latin titles, keep the network + year to find clips.
                const ytQuery = hasArabicTitle
                  ? `${rawTitle} حلقات`.trim()
                  : `${rawTitle} ${network} ${year}`.trim();
                const clipUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(ytQuery)}`;
                const streamUrl = hasArabicTitle
                  ? `https://shahid.mbc.net/en/search-page?query=${encodeURIComponent(rawTitle)}`
                  : (jwFallback
                    ? `https://www.justwatch.com/${jwCountry}/search?q=${encodeURIComponent(jwFallback)}`
                    : null);
                const streamHost = hasArabicTitle ? "shahid" : "justwatch";
                // Wikipedia — always available, adds context. New for TV (previously only on movies).
                const wikiLang = hasArabicTitle ? "ar" : "en";
                const wikiUrl = `https://${wikiLang}.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(rawTitle)}&go=Go`;
                return (
                <li key={i} className="border-b border-dotted border-ink pb-2 last:border-b-0" data-testid={`tv-${i}`}>
                  <div className="font-headline text-lg font-bold leading-tight">{tv.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{tv.network}</div>
                  <div className="font-body italic text-sm text-ink-secondary mt-1">{tv.note}</div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    <a
                      href={clipUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("tv_clip_click", { title: tv.title })}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-press-red hover:underline"
                      data-testid={`tv-clip-${i}`}
                    >
                      ▶ {t.watchClip}
                    </a>
                    {streamUrl && (
                      <a
                        href={streamUrl}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => trackEvent(`tv_${streamHost}_click`, { title: tv.title })}
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red"
                        data-testid={`tv-justwatch-${i}`}
                      >
                        {t.whereToStream} ↗
                      </a>
                    )}
                    <a
                      href={wikiUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("tv_wiki_click", { title: tv.title })}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red"
                      data-testid={`tv-wiki-${i}`}
                    >
                      {t.aboutFilm} ↗
                    </a>
                  </div>
                </li>
              );})}
            </ul>
          </section>

          {/* Books */}
          <section className="md:col-span-6 border-r-2 border-b-2 border-ink p-6 bg-paper-alt tc-fade-up tc-delay-6" data-testid="section-books">
            <SectionTitle icon={<BookOpen size={16} />} label={t.onTheShelf} />
            <ul className="space-y-3">
              {(s.books?.bestsellers || []).map((b, i) => {
                const query = encodeURIComponent(`${b.title_local || b.title} ${b.author}`);
                const domain = config.amazon_domain || "www.amazon.com";
                const amazonUrl = config.amazon_tag
                  ? `https://${domain}/s?k=${query}&tag=${encodeURIComponent(config.amazon_tag)}`
                  : `https://${domain}/s?k=${query}`;
                return (
                  <li key={i} className="flex items-baseline gap-3 border-b border-dotted border-ink pb-2 last:border-b-0" data-testid={`book-${i}`}>
                    <span className="font-headline text-2xl font-black w-8 text-press-green">{i + 1}</span>
                    <div className="flex-1">
                      <div className="font-headline text-lg font-bold leading-tight">{b.title}</div>
                      {b.title_local && b.title_local !== b.title && (
                        <div className="font-headline text-base leading-tight" dir="auto">{b.title_local}</div>
                      )}
                      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{t.by} {b.author}</div>
                      <div className="font-body italic text-sm text-ink-secondary">{b.note}</div>
                      <a
                        href={amazonUrl}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => trackEvent("book_click", { title: b.title, source: "amazon", has_tag: !!config.amazon_tag })}
                        className="inline-block mt-1 font-mono text-[10px] uppercase tracking-widest text-press-red hover:text-press-redHover"
                        data-testid={`book-amazon-${i}`}
                      >
                        Buy on Amazon ↗
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Ambient footer marquee */}
          <section className="md:col-span-12 border-r-2 border-b-2 border-ink bg-ink text-paper overflow-hidden tc-fade-up tc-delay-6" data-testid="section-ambient">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-paper/30">
              <div className="p-5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-paper/70 mb-2"><Fuel size={12} /> {t.gasPrice}</div>
                <div className="font-headline text-2xl">{s.ambient?.gas_price || "—"}</div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-paper/70 mb-2"><TrendingUp size={12} /> {s.ambient?.stock_index?.name || t.ambient}</div>
                <div className="font-headline text-2xl">{s.ambient?.stock_index?.value || "—"}</div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-paper/70 mb-2"><MessageCircle size={12} /> {t.slangOfDay}</div>
                <div className="font-mono text-sm uppercase tracking-widest">{(s.ambient?.slang || []).slice(0, 3).join(" · ")}</div>
              </div>
            </div>
            <div className="border-t border-paper/30 py-2 overflow-hidden">
              <div className="tc-marquee font-mono text-xs uppercase tracking-widest text-paper/90">
                {[...(s.ambient?.trends || []), ...(s.ambient?.slang || [])].concat([...(s.ambient?.trends || []), ...(s.ambient?.slang || [])]).map((t, i) => (
                  <span key={i} className="px-6">◆ {t}</span>
                ))}
              </div>
            </div>
          </section>
        </div>

        <p className="mt-8 font-body italic text-center text-ink-muted">{s.sign_off || "— End of Edition —"}</p>

        <footer className="mt-10 pt-6 border-t-2 border-ink flex flex-col sm:flex-row justify-between gap-2 font-mono text-xs uppercase tracking-widest text-ink-muted">
          <span>TimeCapsule 24 · {meta.long_date}</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <a href="/contact" className="hover:text-press-red" data-testid="footer-contact-link">Contact</a>
            <span aria-hidden="true">·</span>
            <a href="mailto:support@timecapsule24.tv" className="hover:text-press-red">support@timecapsule24.tv</a>
          </div>
        </footer>
      </div>

      {shareOpen && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={() => setShareOpen(false)} data-testid="share-modal">
          <div className="bg-paper-surface border-2 border-ink shadow-hard max-w-2xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b-2 border-ink">
              <h3 className="font-headline text-xl uppercase font-bold">{t.shareTitle}</h3>
              <button onClick={() => setShareOpen(false)} className="font-mono text-xs uppercase tracking-widest border-2 border-ink px-2 py-1 hover:bg-paper-alt" data-testid="close-share-btn">{t.close}</button>
            </div>
            <div className="p-4">
              <div ref={shareRef}>
                <ShareCard snapshot={s} meta={meta} />
              </div>

              {/* Shareable link with OG preview + all channels (WhatsApp, FB, X, Copy, Native) */}
              <div className="mt-4 border-2 border-ink bg-paper-alt p-3" data-testid="share-link-block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">{t.shareLinkLabel}</div>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    readOnly
                    data-testid="share-link-input"
                    value={`${window.location.origin}/api/s/${doc.id}`}
                    className="flex-1 bg-paper-surface border-2 border-ink px-3 py-2 font-mono text-xs sm:text-sm truncate focus:outline-none"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <ShareButtons
                  variant="row"
                  url={`${window.location.origin}/api/s/${doc.id}`}
                  title={`TimeCapsule 24 — ${meta.long_date || ""} in ${meta.region?.name || ""}`}
                  text={`TimeCapsule 24 — ${meta.long_date || ""} in ${meta.region?.name || ""}. Front page: ${(s.news?.[0]?.headline) || ""}`}
                  eventName="dashboard_share"
                />
                <div className="mt-3">
                  <ShareCardButton
                    snapshotId={doc.id}
                    dateLabel={meta.long_date || ""}
                    className="w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-ink font-headline uppercase tracking-widest px-5 py-3 shadow-hard-sm hover:shadow-hard-hover disabled:opacity-70"
                  />
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                  {t.previewCard}
                </div>
              </div>

              <button
                data-testid="download-share-btn"
                onClick={download}
                disabled={downloading}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper font-headline uppercase tracking-widest px-5 py-3 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover disabled:opacity-70"
                style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              >
                <Download size={16} /> {downloading ? t.preparing : t.downloadPng}
              </button>
            </div>
          </div>

          {user && (
            <div className="mt-6">
              <ReferralCard />
            </div>
          )}
        </div>
      )}

      {mediaOpen && (
        <HistoricalMedia
          snapshotId={doc.id}
          meta={meta}
          t={t}
          language={language}
          onClose={() => setMediaOpen(false)}
        />
      )}

      <TVCastOverlay
        open={castOpen}
        onClose={() => setCastOpen(false)}
        url={typeof window !== "undefined" ? `${window.location.origin}/snapshot/${doc.id}` : ""}
        snapshotId={doc.id}
      />
    </div>
  );
}

function SectionTitle({ label, icon }) {
  return (
    <div className="flex items-center gap-2 border-b-2 border-ink pb-2 mb-4">
      {icon}
      <h3 className="font-mono uppercase tracking-[0.2em] text-xs sm:text-sm">— {label} —</h3>
    </div>
  );
}

function LoadingView() {
  const [step, setStep] = useState(0);
  const stages = [
    { label: "Sending pigeons to the archives…", icon: "📜" },
    { label: "Rolling the presses…", icon: "🗞️" },
    { label: "Checking the weather bureau…", icon: "🌤️" },
    { label: "Tuning the radio dial…", icon: "📻" },
    { label: "Wiring in the movie listings…", icon: "🎬" },
    { label: "Chalking up the stock ticker…", icon: "📈" },
    { label: "Setting today's slang…", icon: "💬" },
    { label: "Ink drying — almost there…", icon: "🖋️" },
  ];
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % stages.length), 3200);
    return () => clearInterval(id);
  }, [stages.length]);
  const current = stages[step];
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="loading-view">
      <div className="ink-stamp red mb-6">Assembling the edition…</div>
      <div className="font-headline uppercase text-3xl sm:text-4xl font-black tracking-tight" data-testid="loading-step-headline">{current.label}</div>

      {/* Animated newspaper print effect */}
      <div className="mt-6 w-full max-w-md border-2 border-ink bg-paper-surface shadow-hard overflow-hidden" data-testid="loading-progress-card">
        <div className="p-5">
          <div className="flex items-center gap-3">
            <span className="text-3xl" aria-hidden>{current.icon}</span>
            <div className="text-left flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Step {step + 1} of {stages.length}</div>
              <div className="font-headline text-lg font-bold leading-tight" data-testid="loading-step-label">{current.label}</div>
            </div>
          </div>
          <div className="mt-4 h-2 border-2 border-ink bg-paper-alt overflow-hidden">
            <div
              className="h-full bg-press-red transition-all duration-1000 ease-linear"
              style={{ width: `${((step + 1) / stages.length) * 100}%` }}
              aria-hidden
            />
          </div>
        </div>
        <div className="border-t-2 border-ink bg-ink text-paper py-2 font-mono text-[10px] uppercase tracking-widest text-center">
          ◆ First-time dates take 20–40 seconds · Then cached ◆
        </div>
      </div>

      <p className="mt-6 font-body italic text-ink-muted max-w-md">Gathering real historical headlines, weather, songs on the radio, and the talk of the town.</p>
    </div>
  );
}
